import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, tap } from 'rxjs';
import { IParams } from '../model/http';

export const initItemsParams: any = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
  filters: {},
};

export const initPlannersParams: any = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
};

export const initBackupsParams: any = {
  sort: '',
  order: '',
  search: '',
  take: 25,
  page: 1,
};

export const initDeliveriesParams: any = {
  filters: '',
  take: 10,
  page: 1,
};

export const initPacketsParams: any = {
  filters: '',
  take: 10,
  page: 1,
};

@Injectable({
  providedIn: 'root'
})
export class OlxAdvertService {
  private readonly itemsSubject$ = new BehaviorSubject<any>({});
  public readonly items$ = this.itemsSubject$.asObservable();

  private readonly itemsPlannersSubject$ = new BehaviorSubject<any>({});
  public readonly itemsPlanners$ = this.itemsPlannersSubject$.asObservable();

  private readonly itemsDeliveriesSubject$ = new BehaviorSubject<any>({});
  public readonly itemsDeliveries$ = this.itemsDeliveriesSubject$.asObservable();

  private readonly itemsPacketsSubject$ = new BehaviorSubject<any>({});
  public readonly itemsPackets$ = this.itemsPacketsSubject$.asObservable();

  public readonly isLoader$ = new BehaviorSubject<boolean>(false);

  public queryItemsParams = { ...initItemsParams };
  public queryPlannersParams = { ...initPlannersParams };
  public queryBackupsParams = { ...initBackupsParams };
  public queryDeliveriesParams = { ...initDeliveriesParams };
  public queryPacketsParams = { ...initPacketsParams };


  constructor(
    private readonly http: HttpClient,
  ) { }


  private getAll(params: any) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/adverts`, params)
      .pipe(map(v => v.data));
  }

  private getAllBackups(params: any) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/backup-adverts`, params)
      .pipe(map(v => v.data));
  }

  private getAllDeliveries(params: any) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/deliveries`, { params } )
      .pipe(map(v => v.data));
  }

  public getAllPackets(params: any) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/packets`, { params } )
      .pipe(map(v => v.data));
  }

  getAllAdvertising (channel_id: string, ad_ids: string[]) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/advertising`, { params:  { channel_id, ad_ids }} )
      .pipe(map(v => v.data));
  }

  getOneAdvertising (channel_id: string, ad_ids: string[]) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/advertising/one`, { params:  { channel_id, ad_ids }} )
      .pipe(map(v => v.data));
  }

  public loadItems(channel_id?: string) {
    this.isLoader$.next(true);
    return this.getAll({ channel_id, ...this.queryItemsParams })
      .pipe(tap((items) => {
        this.itemsSubject$.next(items);
        this.isLoader$.next(false);
      }));
  }

  public loadPlanners(channel_id: string) {
    this.isLoader$.next(true);
    return this.getAllPlanners({channel_id, ...this.queryPlannersParams})
      .pipe(tap((v) => {
        this.itemsPlannersSubject$.next(v);
        this.isLoader$.next(false);
      }));
  }

  public loadBackups(channel_id?: string) {
    this.isLoader$.next(true);
    return this.getAllBackups({ channel_id, ...this.queryBackupsParams })
      .pipe(tap((items) => {
        this.itemsSubject$.next(items);
        this.isLoader$.next(false);
      }));
  }

  public loadDeliveries(channel_id?: string) {
    return this.getAllDeliveries({ channel_id, ...this.queryDeliveriesParams })
      .pipe(tap((items) => {
        this.itemsDeliveriesSubject$.next(items);
      }));
  }

  public loadPackets(channel_id?: string) {
    return this.getAllPackets({ channel_id, ...this.queryPacketsParams })
      .pipe(tap((items) => {
        this.itemsPacketsSubject$.next(items);
      }));
  }

  setQueryItemsParams(channel_id: string, params: IParams) {
    const { sort, order, search, take, page, filters } = params;
    this.queryItemsParams = {
      channel_id: channel_id,
      sort: sort || typeof (sort) === 'string' ? sort : this.queryItemsParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryItemsParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryItemsParams.search,
      take: take || this.queryItemsParams.take,
      page: page || this.queryItemsParams.page,
      filters: filters || this.queryItemsParams.filters,
    };

    return this.loadItems();
  }


  setQueryPlannersParams(channel_id: string, params: IParams) {
    const { sort, order, search, take, page, filters } = params;
    this.queryPlannersParams = {
      channel_id: channel_id,
      sort: sort || typeof (sort) === 'string' ? sort : this.queryPlannersParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryPlannersParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryPlannersParams.search,
      take: take || this.queryPlannersParams.take,
      page: page || this.queryPlannersParams.page,
      filters: filters || this.queryItemsParams.filters,
    };

    return this.loadPlanners(channel_id);
  }

  setQueryBackupsParams(channel_id: string, params: IParams) {
    const { sort, order, search, take, page, filters } = params;
    this.queryBackupsParams = {
      channel_id: channel_id,
      sort: sort || typeof (sort) === 'string' ? sort : this.queryBackupsParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryBackupsParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryBackupsParams.search,
      take: take || this.queryBackupsParams.take,
      page: page || this.queryBackupsParams.page,
      filters: filters || this.queryBackupsParams.filters,
    };

    return this.loadBackups();
  }

  setQueryDeliveryParams(channel_id: string, params: IParams) {
    const { page, filters } = params;
    this.queryDeliveriesParams = {
      channel_id: channel_id,
      page: page ?? this.queryDeliveriesParams.page,
      filters: filters ?? this.queryDeliveriesParams.filters,
    };

    return this.loadDeliveries();
  }

  setQueryPacketsParams(channel_id: string, params: IParams) {
    const { page, filters } = params;
    this.queryPacketsParams = {
      channel_id: channel_id,
      page: page ?? this.queryPacketsParams.page,
      filters: filters ?? this.queryPacketsParams.filters,
    };

    return this.loadPackets();
  }

  updateStatus(channel_id: string, advert_id: string, command: string) {
    const url = `${environment.baseURL}/olx-tools/adverts/${advert_id}/command`;
    return this.http.put<any>(url, { channel_id, command })
      .pipe(map(v => v.data));
  }

  updatePrice(channel_id: string, advert_id: string, value: any) {
    const url = `${environment.baseURL}/olx-tools/advert/${advert_id}/`;
    return this.http.put<any>(url, { channel_id, olx: { price: value } })
      .pipe(map(v => v.data));
  }

  updateAdvert(channel_id: string, advert_id: string, olx: any) {
    const url = `${environment.baseURL}/olx-tools/advert/${advert_id}/`;
    return this.http.put<any>(url, { channel_id, olx: olx })
      .pipe(map(v => v.data));
  }

  createAdvert(channel_id: string, advert: any) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/advert`, { channel_id, advert })
      .pipe(map(v => v.data));
  }

  duplicationAdvert(channel_id: string, advert_id: string) {
    const url = `${environment.baseURL}/olx-tools/advert/${advert_id}/duplication`;
    return this.http.put<any>(url, { channel_id })
      .pipe(map(v => v.data));
  }

  deleteAdvert(channel_id: string, advert_id: string) {
    const url = `${environment.baseURL}/olx-tools/advert/${advert_id}/delete`;
    return this.http.put<any>(url, { channel_id })
      .pipe(map(v => v.data));
  }


  updateBulkStatus(channel_id: string, adverts: string[], command: string) {
    const url = `${environment.baseURL}/olx-tools/adverts/command`;
    return this.http.put<any>(url, { channel_id, adverts, command })
      .pipe(map(v => v.data));
  }

  getAllPlanners(params: any) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/planners`, params)
      .pipe(map(v => v.data));
  }

  createPlanner(payload: any) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/adverts/planner`, payload)
      .pipe(map(v => v.data));
  }

  bulkPlanners(payload: any) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/adverts/planner/bulk`, payload)
      .pipe(map(v => v.data));
  }

  cancelBulkPlanners(ids: string[]) {
    return this.http.put<any>(`${environment.baseURL}/olx-tools/adverts/planner/bulk/cancel`, {ids})
      .pipe(map(v => v.data));
  }

  bulkPlannersUpdate(planners: string[], data: any = {}) {
    return this.http.put<any>(`${environment.baseURL}/olx-tools/adverts/planner/bulk`, {planners, ...data})
      .pipe(map(v => v.data));
  }

  updatePlanner(planner_id: string, planned_at: string) {
    return this.http.put<any>(`${environment.baseURL}/olx-tools/adverts/planner`, {planner_id, planned_at})
      .pipe(map(v => v.data));
  }

  cancelPlanner(planner_id: string) {
    return this.http.put<any>(`${environment.baseURL}/olx-tools/adverts/planner/cancel`, { planner_id })
      .pipe(map(v => v.data));
  }

  destroy() {
    this.queryItemsParams = {...initItemsParams};
    this.queryPlannersParams = {...initPlannersParams};
    this.queryBackupsParams = {...initBackupsParams};
    this.queryDeliveriesParams = {...initDeliveriesParams};
  }

  getBalance(channel_id: string) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/balance`, { params:  { channel_id }})
      .pipe(map(v => v.data));
  }

  pushUpAdvert(channel_id: string, advert_id: string) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/advert/${advert_id}/push-up`, { advert_id, channel_id })
      .pipe(map(v => v.data));
  }

  bulkPushUpAdvert(channel_id: string, adverts: string[]) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/adverts/push-up/bulk`, { adverts, channel_id })
      .pipe(map(v => v.data));
  }

  backupRestore(channel_id: string, id: string) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/backup-adverts/restore`, { id, channel_id })
      .pipe(map(v => v.data));
  }

  getLastSync(advert_id: string) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/last-sync`, { advert_id })
      .pipe(map(v => v.data));
  }

  buyPacket(payload: any, channel_id: string) {
    return this.http.post<any>( `${environment.baseURL}/olx-tools/packet`, payload, { params: {
        channel_id,
      }})
      .pipe(map(v => v.data));
  }
  updateStatusPacket(payload: any) {
    return this.http.put<any>(`${environment.baseURL}/olx-tools/status-planner`, payload)
      .pipe(map(v => v.data));
  }

  updatePlanner2(payload: any, event: any) {
    return this.http.put<any>(`${environment.baseURL}/olx-tools/update-planner`, payload, { params: event })
      .pipe(map(v => v.data));
  }

  selectDraftPacket(payload: any, channel_id: string) {
    return this.http.post<any>( `${environment.baseURL}/olx-tools/draft-planner`, payload, { params: {
        channel_id,
      }})
      .pipe(map(v => v.data));
  }


  getZones(category_id: any, channel_id: string) {
    return this.http.get<any>( `${environment.baseURL}/olx-tools/zones`, { params: {
        category_id,
        channel_id,
    }})
      .pipe(map(v => v.data));
  }

  syncAdvert(advert_id: string, channel_id: string) {
    return this.http.put<any>(`${environment.baseURL}/olx-tools/sync-advert`, { advert_id, channel_id})
      .pipe(map(v => v.data));
  }

  getCategoryAttributes(category_id: string, channel_id: string){
    return this.http.get<any>(`${environment.baseURL}/olx-tools/category-attributes`, { params: {category_id, channel_id}})
      .pipe(map(v => v.data));
  }

  getCategories(parent_id: number) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/categories`, { params: { parent_id } })
      .pipe(map(v => v.data));
  }

  getCategoryById(category_id: number) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/categories/${category_id}`)
      .pipe(map(v => v.data));
  }

  getCategoryPhoto(category_id: number) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/category-photo`, { params: { category_id } })
      .pipe(map(v => v.data));
  }

  getPackets(current_category_id: number, channel_id: string, zone_id?: any) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/categories/${current_category_id}/packets`, { params: { channel_id, zone_id } })
      .pipe(map(v => v.data));
  }

  payAds(payload: any, channel_id: string) {
    return this.http.post<any>(`${environment.baseURL}/olx-tools/advertising`, {payload, channel_id })
      .pipe(map(v => v.data));
  }

  getCity(params: any) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/city`, { params })
      .pipe(map(v => v.data));
  }

  getDistrics(params: any) {
    return this.http.get<any>(`${environment.baseURL}/olx-tools/district`, { params })
      .pipe(map(v => v.data));
  }
}
